import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import axios from '../utils/axios';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [memberId, setMemberId] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await axios.post('/members/verify-email', { token });

        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message);
          setMemberId(response.data.data?.member_id || '');

          // Redirect to login after 5 seconds
          setTimeout(() => {
            navigate('/');
          }, 5000);
        }
      } catch (error: any) {
        setStatus('error');
        if (error.response?.data?.error) {
          setMessage(error.response.data.error);
        } else {
          setMessage('Email verification failed. Please try again.');
        }
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white flex items-center justify-center py-12 px-4">
      <div className="card max-w-md w-full text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-secondary-600 no-underline text-sm mb-6 hover:text-secondary-900 transition-colors">
          <ArrowLeft size={20} />
          Back to Home
        </Link>

        {status === 'loading' && (
          <>
            <div className="text-primary-600 mb-6 flex justify-center">
              <Loader2 size={64} className="animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900 mb-4">Verifying Your Email</h1>
            <p className="text-secondary-600">Please wait while we verify your email address...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-green-600 mb-6 flex justify-center">
              <CheckCircle size={64} />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900 mb-4">Email Verified Successfully!</h1>
            <p className="text-secondary-600 mb-6">{message}</p>

            {memberId && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-semibold text-primary-900 mb-2">Welcome to Hammad Foundation!</p>
                <p className="text-sm text-primary-800 mb-2">Your Member ID: <strong>{memberId}</strong></p>
                <p className="text-xs text-primary-700">
                  You can now login and access all member features.
                </p>
              </div>
            )}

            <p className="text-sm text-secondary-600 mb-6">
              Redirecting to login page in 5 seconds...
            </p>

            <Link to="/member/login" className="btn btn-primary no-underline">
              Go to Login
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-red-600 mb-6 flex justify-center">
              <XCircle size={64} />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900 mb-4">Verification Failed</h1>
            <p className="text-secondary-600 mb-6">{message}</p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 mb-2">Possible reasons:</p>
              <ul className="list-disc list-inside text-xs text-red-700 text-left space-y-1">
                <li>The verification link has expired (links are valid for 24 hours)</li>
                <li>The link has already been used</li>
                <li>The link is invalid or corrupted</li>
              </ul>
            </div>

            <div className="flex gap-3 justify-center">
              <Link to="/resend-verification" className="btn btn-secondary no-underline">
                Resend Verification Email
              </Link>
              <Link to="/register" className="btn btn-primary no-underline">
                Register Again
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;
